import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const PROXY = process.env.REACT_APP_API_URL + "api/dashboard";

export const fetchGlobalLeadCount = createAsyncThunk('/dashboard/fetchGlobalLeadCount', async ({ startDate1, endDate1, startDate2, endDate2, source }) => {
    const res = await axios.get(`${PROXY}/get-global-leadcount`, { params: { startDate1, endDate1, startDate2, endDate2, source } });
    return { result: res.data.result };
}
)

export const fetchOemWiseLeadCount = createAsyncThunk(
    '/dashboard/fetchOemWiseLeadCount',
    async ({ startDate1, endDate1, startDate2, endDate2, oem_type, useConversionDateRange, selectedSource }) => {
        const res = await axios.get(`${PROXY}/get-oem-wise-leadcount`, {
            params: { startDate1, endDate1, startDate2, endDate2, oem_type, useConversionDateRange, selectedSource },
        });
        return { result: res.data.result };
    }
);

export const fetchOemWithSourceCount = createAsyncThunk(
    '/dashboard/fetchOemWithSourceCount',
    async ({ startDate1, endDate1, startDate2, endDate2, oem_type }) => {
        const res = await axios.get(`${PROXY}/get-oem-with-source-leadcount`, {
            params: { startDate1, endDate1, startDate2, endDate2, oem_type },
        });
        return { result: res.data.result };
    }
);

export const fetchSourceWiseLeadCount = createAsyncThunk(
    '/dashboard/fetchSourceWiseLeadCount',
    async ({ startDate1, endDate1, startDate2, endDate2, source }) => {
        const res = await axios.get(`${PROXY}/get-source-wise-leadcount`, {
            params: { startDate1, endDate1, startDate2, endDate2, source },
        });
        return { result: res.data.result };
    }
)

export const lineChartCounts = createAsyncThunk(
    '/dashboard/counts-by-period',
    async ({ startDate, endDate, selectedSource, oem_type, useConversionDateRange }) => {
        const res = await axios.get(`${PROXY}/counts-by-period`, {
            params: { startDate, endDate, selectedSource, oem_type, useConversionDateRange },
        });
        return { result: res.data.result };
    }
)

export const fetchSubsourceWiseOemLeadCount = createAsyncThunk(
    '/dashboard/fetchSubsourceWiseOemLeadCount',
    async ({ startDate1, endDate1, oem_type, useConversionDateRange }) => {
        const res = await axios.get(`${PROXY}/get-subsource-wise-oem-leadcount`, {
            params: { startDate: startDate1, endDate: endDate1, oem_type, useConversionDateRange },
        });
        return { result: res.data.result };
    }
);

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: {
        totalLeads1: 0,
        totalLeadsUnique1: 0,
        acceptedLeads1: 0,
        rejectedLeads1: 0,
        unprocessedLeads1: 0,
        totalLeadsUnique1: 0,

        totalLeads2: 0,
        totalLeadsUnique2: 0,
        acceptedLeads2: 0,
        rejectedLeads2: 0,
        unprocessedLeads2: 0,
        totalLeadsUnique2: 0,

        pendingCount: 0,
        isLoading: false,

        oemWisePending: {
            tvs: false,
            ola: false,
            ampere: false,
            revolt: false,
            simple: false,
        },

        internalLoading: {
            total: { r1: false, r2: false },
            accepted: { r1: false, r2: false },
            rejected: { r1: false, r2: false },
            unprocessed: { r1: false, r2: false },
        },

        loadingStates: {
            total: false,
            accepted: false,
            rejected: false,
            unprocessed: false,
        },

        oemSourceData: {},
        oemSourcePending: {
            tvs: false,
            ola: false,
            ampere: false,
            revolt: false,
            simple: false,
        },

        sourceWisePending: 'idle',

        status: 'idle',
        error: null,

        sourceWiseData: {},

        lineChartData: {},

        lineChartPending: false,
        subsourceWiseData: null,
        subsourceWisePending: false,
    },
    reducers: {
        clearRange2Data: (state) => {
            state.totalLeads2 = 0;
            state.totalLeadsUnique2 = 0;
            state.acceptedLeads2 = 0;
            state.rejectedLeads2 = 0;
            state.unprocessedLeads2 = 0;
        }

    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchGlobalLeadCount.pending, (state, action) => {
                state.pendingCount += 1;
                state.isLoading = true;
                state.status = 'loading';

                const { startDate1, endDate1, startDate2, endDate2 } = action.meta.arg;
                const isDual = startDate1 && endDate1 && startDate2 && endDate2;

                ["total", "accepted", "rejected", "unprocessed"].forEach((key) => {
                    state.internalLoading[key].r1 = true;
                    state.internalLoading[key].r2 = isDual;
                    state.loadingStates[key] = true;
                });
            })
            .addCase(fetchGlobalLeadCount.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.pendingCount -= 1;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                const { result } = action.payload;
                const r0 = result.range_0_data || {};
                const r1 = result.range_1_data || {};

                if (result.range_0_data) {
                    state.totalLeads1 = r0.totalLeads || 0;
                    state.totalLeadsUnique1 = r0.totalLeadsUnique || 0;
                    state.acceptedLeads1 = r0.acceptedLeads || 0;
                    state.rejectedLeads1 = r0.rejectedLeads || 0;
                    state.unprocessedLeads1 = r0.unprocessedLeads || 0;
                }

                if (result.range_1_data) {
                    state.totalLeads2 = r1.totalLeads || 0;
                    state.totalLeadsUnique2 = r1.totalLeadsUnique || 0;
                    state.acceptedLeads2 = r1.acceptedLeads || 0;
                    state.rejectedLeads2 = r1.rejectedLeads || 0;
                    state.unprocessedLeads2 = r1.unprocessedLeads || 0;
                }

                ["total", "accepted", "rejected", "unprocessed"].forEach((key) => {
                    state.internalLoading[key].r1 = false;
                    state.internalLoading[key].r2 = false;
                    state.loadingStates[key] = false;
                });
            })
            .addCase(fetchGlobalLeadCount.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;

                state.pendingCount -= 1;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                ["total", "accepted", "rejected", "unprocessed"].forEach((key) => {
                    state.internalLoading[key].r1 = false;
                    state.internalLoading[key].r2 = false;
                    state.loadingStates[key] = false;
                });
            })

            .addCase(fetchOemWiseLeadCount.pending, (state, action) => {
                const oem_type = action.meta.arg.oem_type;
                state.pendingCount += 1;

                if (oem_type === 1) {
                    state.oemWisePending.tvs = true;
                } else if (oem_type === 2) {
                    state.oemWisePending.ola = true;
                } else if (oem_type === 3) {
                    state.oemWisePending.ampere = true;
                } else if (oem_type === 4) {
                    state.oemWisePending.revolt = true;
                } else if (oem_type === 5) {
                    state.oemWisePending.simple = true;
                }
            })
            .addCase(fetchOemWiseLeadCount.fulfilled, (state, action) => {
                const oem_type = action.meta.arg.oem_type;
                const { result } = action.payload;

                state.pendingCount -= 1;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                // initialize if empty
                if (!state.oemWiseData || typeof state.oemWiseData !== "object") {
                    state.oemWiseData = {};
                }

                // 🔥 keep results separated per OEM type (no overwrite)
                state.oemWiseData[oem_type] = result;

                // turn off individual OEM pending flag
                if (oem_type === 1) {
                    state.oemWisePending.tvs = false;
                } else if (oem_type === 2) {
                    state.oemWisePending.ola = false;
                } else if (oem_type === 3) {
                    state.oemWisePending.ampere = false;
                } else if (oem_type === 4) {
                    state.oemWisePending.revolt = false;
                } else if (oem_type === 5) {
                    state.oemWisePending.simple = false;
                }
            })
            .addCase(fetchOemWiseLeadCount.rejected, (state, action) => {
                state.error = action.error.message;

                state.pendingCount -= 1;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                state.oemWisePending.tvs = false;
                state.oemWisePending.ola = false;
                state.oemWisePending.ampere = false;
                state.oemWisePending.revolt = false;
                state.oemWisePending.simple = false;
            })


            .addCase(fetchOemWithSourceCount.pending, (state, action) => {
                const oem_type = action.meta.arg.oem_type;
                state.pendingCount += 1;
                if (!state.oemSourcePending) {
                    state.oemSourcePending = { tvs: false, ola: false, ampere: false, revolt: false, simple: false };
                }

                if (oem_type === 1) state.oemSourcePending.tvs = true;
                else if (oem_type === 2) state.oemSourcePending.ola = true;
                else if (oem_type === 3) state.oemSourcePending.ampere = true;
                else if (oem_type === 4) state.oemSourcePending.revolt = true;
                else if (oem_type === 5) state.oemSourcePending.simple = true;
            })

            .addCase(fetchOemWithSourceCount.fulfilled, (state, action) => {
                const oem_type = action.meta.arg.oem_type;
                const { result } = action.payload || {};

                if (!result) return; // ✅ skip if undefined or invalid

                state.pendingCount -= 1;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                if (!state.oemSourceData || typeof state.oemSourceData !== "object") {
                    state.oemSourceData = {};
                }

                state.oemSourceData[oem_type] = result;

                if (oem_type === 1) state.oemSourcePending.tvs = false;
                else if (oem_type === 2) state.oemSourcePending.ola = false;
                else if (oem_type === 3) state.oemSourcePending.ampere = false;
                else if (oem_type === 4) state.oemSourcePending.revolt = false;
                else if (oem_type === 5) state.oemSourcePending.simple = false;
            })

            .addCase(fetchOemWithSourceCount.rejected, (state, action) => {
                state.error = action.error.message;
                state.pendingCount -= 1;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                // reset all OEM pending flags
                if (!state.oemSourcePending) {
                    state.oemSourcePending = { tvs: false, ola: false, ampere: false, revolt: false, simple: false };
                } else {
                    Object.keys(state.oemSourcePending).forEach(k => (state.oemSourcePending[k] = false));
                }
            })

            .addCase(fetchSourceWiseLeadCount.pending, (state) => {
                state.pendingCount += 1;
                state.sourceWisePending = true;
            })
            .addCase(fetchSourceWiseLeadCount.fulfilled, (state, action) => {
                state.sourceWisePending = false;

                state.pendingCount -= 1;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                state.sourceWiseData = action.payload.result;
            })
            .addCase(fetchSourceWiseLeadCount.rejected, (state, action) => {
                state.sourceWisePending = false;
                state.pendingCount -= 1;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                state.error = action.error.message;
            })
            .addCase(lineChartCounts.pending, (state) => {
                state.pendingCount += 1;
                state.lineChartPending = true;
                state.status = 'loading';
            })
            .addCase(lineChartCounts.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.pendingCount -= 1;
                state.lineChartPending = false;

                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }

                const { result } = action.payload;
                state.lineChartData = result;
            })
            .addCase(lineChartCounts.rejected, (state, action) => {
                state.error = action.error.message;
                state.pendingCount -= 1;
                state.lineChartPending = false;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }
            })
            .addCase(fetchSubsourceWiseOemLeadCount.pending, (state) => {
                state.pendingCount += 1;
                state.subsourceWisePending = true;
            })
            .addCase(fetchSubsourceWiseOemLeadCount.fulfilled, (state, action) => {
                state.pendingCount -= 1;
                state.subsourceWisePending = false;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }
                state.subsourceWiseData = action.payload.result;
            })
            .addCase(fetchSubsourceWiseOemLeadCount.rejected, (state, action) => {
                state.pendingCount -= 1;
                state.subsourceWisePending = false;
                if (state.pendingCount === 0) {
                    state.isLoading = false;
                }
                state.error = action.error.message;
            })
    }

})

export default dashboardSlice.reducer;
export const { clearRange2Data } = dashboardSlice.actions;